import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Check,
  X,
  CheckCircle2,
  XCircle,
  Clock,
  Filter,
  CalendarRange,
} from 'lucide-react';
import Lottie from 'lottie-react';
import loadingAnimation from '@/assets/animations/loading-blue.json';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAllRequests,
  useChangeRequest,
  useReviewRequest,
  type RequestFilterParams,
  type ChangeRequest,
  type ChangeRequestListItem,
  type ChangeRequestItem,
} from '@/hooks/schedule-change-requests/useScheduleChangeRequests';
import { usePermissions } from '@/hooks/usePermissions';
import { useOrganizationsForSelect } from '@/hooks/organizations/useOrganizations';
import { useDepartmentMonthSchedule } from '@/hooks/schedules/useDepartmentSchedule';
import {
  SchedulePreviewGrid,
  type EmployeeRow,
} from '@/components/schedule-change-requests/SchedulePreviewGrid';
import { type PreviewDay } from '@/utils/generateTemplatePreview';
import { useTemplatesCalendarBatch } from '@/hooks/schedules/useTemplateCalendar';
import { APP_TIME_ZONE } from '@/utils/dateFormat';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MONTH_NAMES = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

type RequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

const STATUS_CONFIG: Record<
  RequestStatus,
  { label: string; className: string }
> = {
  pending: {
    label: 'Ожидает',
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  approved: {
    label: 'Одобрена',
    className: 'bg-green-100 text-green-800 border-green-200',
  },
  rejected: {
    label: 'Отклонена',
    className: 'bg-red-100 text-red-800 border-red-200',
  },
  cancelled: {
    label: 'Отменена',
    className: 'bg-gray-100 text-gray-800 border-gray-200',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    timeZone: APP_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    timeZone: APP_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function fullName(person: {
  first_name: string;
  last_name: string;
  middle_name?: string | null;
}): string {
  return [person.last_name, person.first_name, person.middle_name]
    .filter(Boolean)
    .join(' ');
}

function getStatusBadge(status: string) {
  const config = STATUS_CONFIG[status as RequestStatus] ?? STATUS_CONFIG.pending;
  return <Badge className={config.className}>{config.label}</Badge>;
}

function getDatesForMonth(year: number, month: number): Date[] {
  // month is 1-indexed here
  const daysInMonth = new Date(year, month, 0).getDate();
  const dates: Date[] = [];
  for (let d = 1; d <= daysInMonth; d++) {
    dates.push(new Date(year, month - 1, d));
  }
  return dates;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const RequestsTableSkeleton: React.FC = () => (
  <div className="space-y-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <Skeleton key={i} className="h-12 w-full rounded-md" />
    ))}
  </div>
);

const DetailSkeleton: React.FC = () => (
  <div className="space-y-4 p-6">
    <div className="flex gap-4">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-6 w-32" />
      <Skeleton className="h-6 w-24" />
    </div>
    <Skeleton className="h-[400px] w-full rounded-lg" />
  </div>
);

// ---------------------------------------------------------------------------
// Filters Component
// ---------------------------------------------------------------------------

interface FiltersProps {
  filter: RequestFilterParams;
  onFilterChange: (partial: Partial<RequestFilterParams>) => void;
}

const RequestFilters: React.FC<FiltersProps> = ({ filter, onFilterChange }) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];
  const { data: organizations = [] } = useOrganizationsForSelect();

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Filter className="h-4 w-4" />
          Фильтры
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-3">
          {/* Status */}
          <Select
            value={filter.status ?? '_all'}
            onValueChange={(val) =>
              onFilterChange({ status: val === '_all' ? undefined : val })
            }
          >
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Все статусы" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Все статусы</SelectItem>
              <SelectItem value="pending">Ожидает</SelectItem>
              <SelectItem value="approved">Одобрена</SelectItem>
              <SelectItem value="rejected">Отклонена</SelectItem>
              <SelectItem value="cancelled">Отменена</SelectItem>
            </SelectContent>
          </Select>

          {/* Organization */}
          <Select
            value={filter.organization_id ?? '_all'}
            onValueChange={(val) =>
              onFilterChange({
                organization_id: val === '_all' ? undefined : val,
              })
            }
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Все организации" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Все организации</SelectItem>
              {organizations.map((org: { id: string; name: string }) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Year */}
          <Select
            value={filter.year != null ? String(filter.year) : '_all'}
            onValueChange={(val) =>
              onFilterChange({
                year: val === '_all' ? undefined : Number(val),
              })
            }
          >
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Год" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Все года</SelectItem>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Month */}
          <Select
            value={filter.month != null ? String(filter.month) : '_all'}
            onValueChange={(val) =>
              onFilterChange({
                month: val === '_all' ? undefined : Number(val),
              })
            }
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Все месяцы" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Все месяцы</SelectItem>
              {MONTH_NAMES.map((name, i) => (
                <SelectItem key={i} value={String(i + 1)}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Requests List
// ---------------------------------------------------------------------------

interface RequestsListProps {
  requests: ChangeRequestListItem[] | undefined;
  isLoading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const RequestsList: React.FC<RequestsListProps> = ({
  requests,
  isLoading,
  selectedId,
  onSelect,
}) => {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Заявки</CardTitle>
        </CardHeader>
        <CardContent>
          <RequestsTableSkeleton />
        </CardContent>
      </Card>
    );
  }

  if (!requests || requests.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Заявки</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <Clock className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-3 text-sm font-semibold text-muted-foreground">
              Заявки не найдены
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Нет заявок на изменение графиков с выбранными фильтрами.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          Заявки ({requests.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">Дата</th>
                <th className="px-4 py-3 text-left font-medium">Менеджер</th>
                <th className="px-4 py-3 text-left font-medium">
                  Организация
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  Подразделение
                </th>
                <th className="px-4 py-3 text-left font-medium">Период</th>
                <th className="px-4 py-3 text-center font-medium">Кол-во</th>
                <th className="px-4 py-3 text-center font-medium">Статус</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => {
                const isSelected = req.id === selectedId;

                return (
                  <tr
                    key={req.id}
                    onClick={() => onSelect(req.id)}
                    className={cn(
                      'border-b cursor-pointer transition-colors',
                      isSelected
                        ? 'bg-blue-50 hover:bg-blue-50'
                        : 'hover:bg-muted/50',
                    )}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      {formatShortDate(req.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      {req.requested_by
                        ? fullName(req.requested_by)
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {req.department.organization?.name ?? '-'}
                    </td>
                    <td className="px-4 py-3">{req.department.name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {formatShortDate(req.valid_from)} – {formatShortDate(req.valid_until)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant="secondary">{req.items_count}</Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getStatusBadge(req.status)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Item Review Buttons
// ---------------------------------------------------------------------------

interface ItemDecisionButtonsProps {
  itemId: string;
  currentDecision: 'approved' | 'rejected' | undefined;
  onDecision: (itemId: string, decision: 'approved' | 'rejected') => void;
  disabled?: boolean;
}

const ItemDecisionButtons: React.FC<ItemDecisionButtonsProps> = ({
  itemId,
  currentDecision,
  onDecision,
  disabled = false,
}) => (
  <div className="flex items-center gap-1">
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        'h-7 w-7',
        currentDecision === 'approved'
          ? 'bg-green-100 text-green-700 hover:bg-green-200'
          : 'text-muted-foreground hover:text-green-600',
      )}
      onClick={(e) => {
        e.stopPropagation();
        onDecision(itemId, 'approved');
      }}
      disabled={disabled}
      title="Одобрить"
    >
      <CheckCircle2 className="h-4 w-4" />
    </Button>
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        'h-7 w-7',
        currentDecision === 'rejected'
          ? 'bg-red-100 text-red-700 hover:bg-red-200'
          : 'text-muted-foreground hover:text-red-600',
      )}
      onClick={(e) => {
        e.stopPropagation();
        onDecision(itemId, 'rejected');
      }}
      disabled={disabled}
      title="Отклонить"
    >
      <XCircle className="h-4 w-4" />
    </Button>
  </div>
);

// ---------------------------------------------------------------------------
// Request Detail View
// ---------------------------------------------------------------------------

interface RequestDetailProps {
  requestId: string;
  onClose: () => void;
  canReview: boolean;
}

const RequestDetail: React.FC<RequestDetailProps> = ({
  requestId,
  onClose,
  canReview,
}) => {
  const {
    data: request,
    isLoading: requestLoading,
    error: requestError,
  } = useChangeRequest(requestId);

  // Load department schedule for the request's month (month is 0-indexed in the hook)
  const {
    data: deptSchedule,
    isLoading: scheduleLoading,
  } = useDepartmentMonthSchedule(
    request?.department.id ?? null,
    request?.year,
    request ? request.month - 1 : undefined, // Convert 1-indexed to 0-indexed
  );

  const reviewMutation = useReviewRequest();

  // Local review decisions: default all items to 'approved'
  const [reviewDecisions, setReviewDecisions] = useState<
    Map<string, 'approved' | 'rejected'>
  >(new Map());
  const [reviewComment, setReviewComment] = useState('');

  // Reset decisions when request data loads
  useEffect(() => {
    if (request?.items) {
      const defaults = new Map<string, 'approved' | 'rejected'>();
      for (const item of request.items) {
        if (item.status === 'pending') {
          defaults.set(item.id, 'approved');
        }
      }
      setReviewDecisions(defaults);
      setReviewComment('');
    }
  }, [request?.id, request?.items]);

  const isPending = request?.status === 'pending';

  // Build dates array for the month
  const dates = useMemo(() => {
    if (!request) return [];
    return getDatesForMonth(request.year, request.month);
  }, [request?.year, request?.month]);

  // Build employee rows from department schedule, merging with request items
  const changedEmployeeIds = useMemo(() => {
    if (!request?.items) return new Set<string>();
    return new Set(request.items.map((item) => item.employee.id));
  }, [request?.items]);

  // Fetch calendars for all new templates from template_calendars (source of truth)
  const uniqueTemplateIds = useMemo(() => {
    if (!request?.items) return [];
    const ids = new Set<string>();
    for (const item of request.items) {
      if (item.new_template?.id) ids.add(item.new_template.id);
    }
    return Array.from(ids);
  }, [request?.items]);

  const { calendars: templateCalendars } = useTemplatesCalendarBatch(
    uniqueTemplateIds,
    request?.year ?? new Date().getFullYear(),
    request?.month ?? new Date().getMonth() + 1,
  );

  // Build preview schedules for changed employees
  const previewSchedules = useMemo(() => {
    const map = new Map<string, PreviewDay[]>();
    if (!request?.items || !request.year || !request.month) return map;

    for (const item of request.items) {
      const templateId = item.new_template?.id;
      if (!templateId) continue;
      const cal = templateCalendars.get(templateId);
      if (!cal) continue;
      const preview: PreviewDay[] = cal.map((c) => {
        const shiftStart = c.shift_start ? String(c.shift_start) : null;
        const shiftEnd = c.shift_end ? String(c.shift_end) : null;
        return {
          date: String(c.date).slice(0, 10),
          is_working_day: !!c.is_working_day,
          shift_start: shiftStart,
          shift_end: shiftEnd,
          schedule_type: c.schedule_type || (c.is_working_day ? 'working' : 'day_off'),
        };
      });
      map.set(item.employee.id, preview);
    }
    return map;
  }, [request?.items, request?.year, request?.month, templateCalendars]);

  // Map change request items by employee id for quick lookup
  const itemsByEmployeeId = useMemo(() => {
    const map = new Map<string, ChangeRequestItem>();
    if (!request?.items) return map;
    for (const item of request.items) {
      map.set(item.employee.id, item);
    }
    return map;
  }, [request?.items]);

  // Build employee rows: all department employees + mark changed ones
  const employeeRows: EmployeeRow[] = useMemo(() => {
    if (!deptSchedule?.employees) return [];

    return deptSchedule.employees.map((emp) => {
      const changeItem = itemsByEmployeeId.get(emp.employee_id);
      const hasChange = !!changeItem;

      return {
        id: emp.employee_id,
        first_name: emp.first_name,
        last_name: emp.last_name,
        position: emp.position ?? null,
        schedule: emp.schedule,
        total_working_days: emp.total_working_days,
        total_days_off: emp.total_days_off,
        hasChange,
        changeLabel: hasChange && changeItem?.new_template
          ? changeItem.new_template.name
          : undefined,
      };
    });
  }, [deptSchedule?.employees, itemsByEmployeeId]);

  // Handlers
  const handleDecision = useCallback(
    (itemId: string, decision: 'approved' | 'rejected') => {
      setReviewDecisions((prev) => {
        const next = new Map(prev);
        // Toggle: if already set to this decision, just keep it (no toggle off)
        next.set(itemId, decision);
        return next;
      });
    },
    [],
  );

  const handleApproveAll = useCallback(() => {
    if (!request?.items) return;
    const next = new Map<string, 'approved' | 'rejected'>();
    for (const item of request.items) {
      if (item.status === 'pending') {
        next.set(item.id, 'approved');
      }
    }
    setReviewDecisions(next);
  }, [request?.items]);

  const handleRejectAll = useCallback(() => {
    if (!request?.items) return;
    const next = new Map<string, 'approved' | 'rejected'>();
    for (const item of request.items) {
      if (item.status === 'pending') {
        next.set(item.id, 'rejected');
      }
    }
    setReviewDecisions(next);
  }, [request?.items]);

  const handleSubmitReview = useCallback(async () => {
    if (!request) return;

    const items: { item_id: string; status: 'approved' | 'rejected' }[] = [];
    for (const [itemId, status] of reviewDecisions) {
      items.push({ item_id: itemId, status });
    }

    if (items.length === 0) return;

    await reviewMutation.mutateAsync({
      requestId: request.id,
      data: {
        items,
        comment: reviewComment.trim() || undefined,
      },
    });
  }, [request, reviewDecisions, reviewComment, reviewMutation]);

  // Count decisions
  const approvedCount = useMemo(
    () =>
      Array.from(reviewDecisions.values()).filter((d) => d === 'approved')
        .length,
    [reviewDecisions],
  );
  const rejectedCount = useMemo(
    () =>
      Array.from(reviewDecisions.values()).filter((d) => d === 'rejected')
        .length,
    [reviewDecisions],
  );

  // Loading
  if (requestLoading || scheduleLoading) {
    return (
      <Card className="mt-4">
        <CardContent className="flex items-center justify-center py-12">
          <Lottie animationData={loadingAnimation} loop className="w-48 h-48" />
        </CardContent>
      </Card>
    );
  }

  if (requestError || !request) {
    return (
      <Card className="mt-4">
        <CardContent className="py-8 text-center text-muted-foreground">
          Не удалось загрузить заявку. Попробуйте позже.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2 flex-1 min-w-0">
            <CardTitle className="text-lg">
              Заявка: {request.department.name}
            </CardTitle>
            <div className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm">
              <CalendarRange className="h-4 w-4 text-blue-600" />
              <span className="text-blue-900">
                <span className="font-medium">Период действия:</span>{' '}
                {formatShortDate(request.valid_from)} – {formatShortDate(request.valid_until)}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span>
                Создал:{' '}
                <span className="font-medium text-foreground">
                  {fullName(request.requested_by)}
                </span>
              </span>
              <span>{formatDate(request.created_at)}</span>
              {getStatusBadge(request.status)}
              {request.reviewed_by && (
                <span>
                  Рассмотрел:{' '}
                  <span className="font-medium text-foreground">
                    {fullName(request.reviewed_by)}
                  </span>
                </span>
              )}
              {request.reviewed_at && (
                <span>{formatDate(request.reviewed_at)}</span>
              )}
            </div>
            {request.comment && (
              <p className="text-sm text-muted-foreground mt-1">
                Комментарий: {request.comment}
              </p>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} title="Закрыть">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Changed items list with review controls */}
        {request.items && request.items.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="px-4 py-2 text-left font-medium">
                    Сотрудник
                  </th>
                  <th className="px-4 py-2 text-left font-medium">
                    Текущий шаблон
                  </th>
                  <th className="px-4 py-2 text-left font-medium">
                    Новый шаблон
                  </th>
                  <th className="px-4 py-2 text-center font-medium">Статус</th>
                  {isPending && canReview && (
                    <th className="px-4 py-2 text-center font-medium">
                      Решение
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {request.items.map((item) => {
                  const decision = reviewDecisions.get(item.id);
                  const itemIsPending = item.status === 'pending';

                  return (
                    <tr key={item.id} className="border-b last:border-b-0">
                      <td className="px-4 py-2">
                        <div>
                          <span className="font-medium">
                            {fullName(item.employee)}
                          </span>
                          {item.employee.position && (
                            <span className="block text-xs text-muted-foreground">
                              {item.employee.position.name}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {item.current_template?.name ?? '-'}
                      </td>
                      <td className="px-4 py-2">
                        <span className="font-medium text-blue-700">
                          {item.new_template?.name ?? '-'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        {getStatusBadge(item.status)}
                      </td>
                      {isPending && canReview && (
                        <td className="px-4 py-2 text-center">
                          {itemIsPending ? (
                            <ItemDecisionButtons
                              itemId={item.id}
                              currentDecision={decision}
                              onDecision={handleDecision}
                              disabled={reviewMutation.isPending}
                            />
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              -
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Calendar grid showing all employees */}
        {employeeRows.length > 0 && dates.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-2">
              График отдела на {MONTH_NAMES[request.month - 1]} {request.year}
            </h3>
            <SchedulePreviewGrid
              employees={employeeRows}
              dates={dates}
              previewSchedules={previewSchedules}
              showDiff={true}
              selectedEmployeeIds={changedEmployeeIds}
            />
          </div>
        )}

        {/* Review actions (only for pending requests and users with write permission) */}
        {isPending && canReview && request.items && request.items.length > 0 && (
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-muted-foreground">Быстрый выбор:</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleApproveAll}
                disabled={reviewMutation.isPending}
                className="gap-1"
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                Одобрить все
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectAll}
                disabled={reviewMutation.isPending}
                className="gap-1"
              >
                <XCircle className="h-3.5 w-3.5 text-red-600" />
                Отклонить все
              </Button>
            </div>

            {/* Comment */}
            <div>
              <label
                htmlFor="review-comment"
                className="text-sm font-medium mb-1 block"
              >
                Комментарий (необязательно)
              </label>
              <Input
                id="review-comment"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Комментарий к решению..."
                disabled={reviewMutation.isPending}
              />
            </div>

            {/* Submit */}
            <div className="flex items-center gap-3">
              <Button
                onClick={handleSubmitReview}
                disabled={
                  reviewMutation.isPending || reviewDecisions.size === 0
                }
                className="gap-1"
              >
                <Check className="h-4 w-4" />
                {reviewMutation.isPending
                  ? 'Сохранение...'
                  : 'Применить выбранные'}
              </Button>
              <span className="text-sm text-muted-foreground">
                {approvedCount > 0 && (
                  <span className="text-green-700">
                    {approvedCount} одобрено
                  </span>
                )}
                {approvedCount > 0 && rejectedCount > 0 && ', '}
                {rejectedCount > 0 && (
                  <span className="text-red-700">
                    {rejectedCount} отклонено
                  </span>
                )}
              </span>
            </div>

            {reviewMutation.isError && (
              <p className="text-sm text-red-600">
                Ошибка при сохранении решения. Попробуйте ещё раз.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export const ScheduleChangeRequests: React.FC = () => {
  const location = useLocation();
  const [filter, setFilter] = useState<RequestFilterParams>({});
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null,
  );
  const [showSuccess, setShowSuccess] = useState(false);

  const detailRef = useRef<HTMLDivElement>(null);

  const { data: requests, isLoading } = useAllRequests(filter);
  const { hasPermission, isAdmin } = usePermissions();
  // Рассматривать заявки могут Admin и HR (у которых есть employees:write),
  // но не управляющие (у них employees только read)
  const canReview = isAdmin() || hasPermission('employees', 'write');

  // Показать success-сообщение если перешли со страницы создания заявки
  useEffect(() => {
    if ((location.state as { success?: boolean })?.success) {
      setShowSuccess(true);
      // Очистить state чтобы при обновлении страницы не показывалось снова
      window.history.replaceState({}, '');
      const timer = setTimeout(() => setShowSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const handleFilterChange = useCallback(
    (partial: Partial<RequestFilterParams>) => {
      setFilter((prev) => ({ ...prev, ...partial }));
      setSelectedRequestId(null); // Reset selection on filter change
    },
    [],
  );

  const handleSelectRequest = useCallback((id: string) => {
    setSelectedRequestId((prev) => (prev === id ? null : id));
  }, []);

  // Scroll to detail section when a request is selected
  useEffect(() => {
    if (selectedRequestId && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedRequestId]);

  const handleCloseDetail = useCallback(() => {
    setSelectedRequestId(null);
  }, []);

  return (
    <div className="space-y-4 flex-1 overflow-auto p-1">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Заявки на изменение графиков
        </h1>
        <p className="text-muted-foreground">
          {canReview
            ? 'Просмотр и рассмотрение заявок на изменение графиков от менеджеров подразделений'
            : 'Просмотр заявок на изменение графиков'}
        </p>
      </div>

      {/* Success message after creating a request */}
      {showSuccess && (
        <Alert className="border-green-300 bg-green-50">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <AlertTitle className="text-green-800">Заявка отправлена</AlertTitle>
          <AlertDescription className="text-green-700">
            Заявка на изменение графиков успешно отправлена на рассмотрение HR.
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <RequestFilters filter={filter} onFilterChange={handleFilterChange} />

      {/* Requests list */}
      <RequestsList
        requests={requests}
        isLoading={isLoading}
        selectedId={selectedRequestId}
        onSelect={handleSelectRequest}
      />

      {/* Detail view */}
      {selectedRequestId && (
        <div ref={detailRef}>
          <RequestDetail
            key={selectedRequestId}
            requestId={selectedRequestId}
            onClose={handleCloseDetail}
            canReview={canReview}
          />
        </div>
      )}
    </div>
  );
};

export default ScheduleChangeRequests;
